import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';

import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import {
  useBiqChatHistory,
  HISTORY_ERROR,
  HISTORY_LOADING,
} from '../context/BiqChatHistoryContext';
import { useBusyChatIds, useUnreadChatIds } from '../context/BiqLiveChatsContext';

const SidebarChatHistory = ({ onSelectChat }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const { chats, historyStatus, reloadChats, activeChatId, selectChat } = useBiqChatHistory();
  const busyIds = useBusyChatIds();
  const unreadIds = useUnreadChatIds();
  const [query, setQuery] = useState('');

  const trimmedQuery = query.trim();
  const busyIdSet = useMemo(() => new Set(busyIds), [busyIds]);
  const unreadIdSet = useMemo(() => new Set(unreadIds), [unreadIds]);

  const visibleChats = useMemo(() => {
    if (!trimmedQuery) return chats;
    const needle = trimmedQuery.toLowerCase();
    return chats.filter((chat) => chat.title.toLowerCase().includes(needle));
  }, [chats, trimmedQuery]);

  const handleSelect = (id) => {
    selectChat(id);
    if (onSelectChat) onSelectChat();
  };

  return (
    <div className="mt-[16px]">
      <div className="relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-[12px] top-1/2 -translate-y-1/2 text-[#828382]"
        />
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('nav.searchChats')}
          aria-label={t('nav.searchChats')}
          className="h-[40px] w-full rounded-[9px] border border-[#E2E2E2] bg-[#F8F9F8] pl-[36px] pr-[12px] font-['Inter'] text-[#444746] outline-none transition-colors placeholder:text-[#828382] focus:border-[#16A34A] dark:border-[#282a29] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
          style={{ fontSize }}
        />
      </div>

      <div className="mt-[16px] flex items-center gap-[8px]">
        <span className="h-px flex-1 bg-[#E2E2E2] dark:bg-[#282a29]" />
        <span className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.08em] text-[#828382]">
          {t('nav.recentChats')}
        </span>
        <span className="h-px flex-1 bg-[#E2E2E2] dark:bg-[#282a29]" />
      </div>

      {visibleChats.length === 0 ? (
        // Order matters: a search with no hits is about the query, not the load
        <div className="px-[19px] pt-[8px] pb-[21px] text-center">
          {trimmedQuery ? (
            <p className="font-['Inter'] text-[#828382]" style={{ fontSize }}>
              {t('nav.noChatsFound')}
            </p>
          ) : historyStatus === HISTORY_ERROR ? (
            <>
              <p className="font-['Inter'] text-[#828382]" style={{ fontSize }}>
                {t('nav.chatsLoadFailed')}
              </p>
              <button
                type="button"
                onClick={reloadChats}
                className="mt-[8px] cursor-pointer rounded-[8px] px-[12px] py-[4px] font-['Inter'] text-[#16A34A] underline underline-offset-2 transition-colors hover:bg-[#E8EDEA] dark:hover:bg-[#282a29]/50"
                style={{ fontSize }}
              >
                {t('nav.retryLoadChats')}
              </button>
            </>
          ) : (
            <p className="font-['Inter'] text-[#828382]" style={{ fontSize }}>
              {historyStatus === HISTORY_LOADING ? t('nav.loadingChats') : t('nav.noChatsYet')}
            </p>
          )}
        </div>
      ) : (
        <ul className="max-h-[320px] overflow-y-auto overflow-x-hidden scrollbar-hide pt-[8px] pb-[21px]">
          {visibleChats.map((chat) => {
            const isActive = chat.id === activeChatId;
            const isGenerating = busyIdSet.has(chat.id);
            const isUnread = unreadIdSet.has(chat.id);
            return (
              <li key={chat.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(chat.id)}
                  title={chat.title}
                  aria-current={isActive ? 'true' : undefined}
                  aria-label={
                    isGenerating
                      ? `${chat.title}. ${t('nav.chatGenerating')}`
                      : isUnread
                        ? `${chat.title}. ${t('nav.chatReady')}`
                        : undefined
                  }
                  className={`flex w-full cursor-pointer items-center gap-[8px] rounded-[8px] px-[12px] py-[8px] text-left font-['Inter'] transition-colors ${
                    isActive
                      ? 'bg-[#DAEDE1] text-[#16A34A] dark:bg-[#173322] dark:text-[#7EE6A4]'
                      : 'text-[#444746] hover:bg-[#E8EDEA] dark:text-[#f0f5f2] dark:hover:bg-[#282a29]/50'
                  }`}
                  // ponytail: sidebar rows use a fixed offset below the
                  // global content font-size, not the raw setting - ChatGPT/
                  // Gemini/Claude all use a small fixed sidebar font
                  // independent of chat-text size (their own titles stay
                  // short, but they don't scale the row itself either).
                  // Prevents a user on a large accessibility font from
                  // clipping titles that fit fine at default.
                  style={{ fontSize: Math.max(10, Number(fontSize) - 2) }}
                >
                  <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                  {isGenerating ? (
                    <Loader2
                      size={12}
                      aria-hidden="true"
                      className="shrink-0 animate-spin text-[#16A34A] dark:text-[#7EE6A4]"
                    />
                  ) : isUnread ? (
                    <span
                      aria-hidden="true"
                      className="h-[6px] w-[6px] shrink-0 rounded-full bg-[#16A34A] dark:bg-[#7EE6A4]"
                    />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SidebarChatHistory;
